import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import Mustache from 'mustache';
import { useSelector } from 'react-redux';
import './KycFormsView.scss';
import { PageHeading } from '../../../../../../../../SharedComponents';
import {
  CustomButton,
  KYCSwitcher,
  KYC_TAB_MODES,
  KYCHistory,
  CustomIconButton,
} from '../../../../../../../../Components';
import btnLoader from '../../../../../../../../assets/images/GIF_images/btn-loader.gif';
import { IndividualForm } from './Forms/IndividualForm/IndividualForm';
import {
  GetKycFormDetailsByContactId,
  ShareKycDocument,
  UpdateKycDetails,
} from '../../../../../../../../Services';
import {
  bottomBoxComponentUpdate,
  GetParams,
  showError,
  showSuccess,
  showWarn,
} from '../../../../../../../../Helper';
import { useLayoutDetection } from '../../../../../../../../Hooks';
import { CorporateForm } from './Forms/CorporateForm/CorporateForm';
import { ButtonBase } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { IndividualKycFormMap } from './Forms/IndividualForm/IndividualKycFormMap';
import { CorporateKycFormMap } from './Forms/CorporateForm/CorporateKycFormMap';
import { KYCFormHTMLTemplate } from './Forms/Utilities/KYCFormHTMLTemplate';
import { NewKYCFormHTMLTemplate } from './Forms/Utilities/NewKYCFormHTMLTemplate';

// Icons
import {
  PrintIcon,
  WhatsappIcon,
  EmailIcon,
  ShareIcon,
} from '../../../../../../../../assets/icons';
import { useVerticalNav } from '../../../../../../../../Contexts/VerticalNavContext';

export const KycFormsView = ({
  parentTranslationPath = '',
  translationPath = '',
}) => {
  const contactId = GetParams('id');
  const contactsType = +GetParams('formType');
  const { t } = useTranslation(parentTranslationPath);
  const clientFormRef = useRef(null);
  const { isNewLayout, hasNewLayout } = useLayoutDetection();
  const verticalNav = useVerticalNav();
  const { isDarkMode } = useSelector((state) => state.theme);
  const [checkFormValidation, setCheckFormValidation] = useState(false);
  const isPsiAssetsCRM = window.location.pathname.includes('psiassets-crm');

  const reducer = useCallback((state, action) => {
    if (action.id !== 'edit') return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const ShareViaEnum = {
    Email: {
      id: 1,
      name: 'Email',
    },
    WhatsApp: {
      id: 2,
      name: 'WhatsApp',
    },
    Manual: {
      id: 3,
      name: 'Manual',
    },
  };

  const [selected, setSelected] = useReducer(reducer, {});
  const [isLoading, setIsLoading] = useReducer(reducer, {
    save: false,
    get: false,
    discard: false,
    shareForm: false,
  });
  const [viewMode, setViewMode] = useState(KYC_TAB_MODES.KYC);

  const onChangeViewMode = (value) => {
    setViewMode(value);
  };

  const clearPEPFields = () => {
    setSelected({ id: 'nameOfPEP', value: '' });
  };
  const clearOnBehalfFields = () => {
    setSelected({
      id: 'edit',
      value: {
        ...selected,
        nameOfBeneficiary: '',
        natureOfRelationship: '',
        idType: '',
        nationalityOfBeneficiaryInfo: null,
        isBeneficiaryPEP: null,
        emailAddressOfBeneficiary: '',
        contactNumberOfBeneficiary: '',
      },
    });
  };

  const getKYCIndividualFormDto = () => {
    const occupationLookupId =
      selected.occupationInfo?.lookupItemId ||
      selected.occupationInfo?.lookupsId ||
      null;
    const occupationInfo = occupationLookupId
      ? {
          lookupItemName: selected.occupationInfo?.lookupItemName || null,
          lookupsId: occupationLookupId,
        }
      : null;

    const sourceOfFundLookupId =
      selected.sourceOfFundInfo?.lookupItemId ||
      selected.sourceOfFundInfo?.lookupsId ||
      null;
    const sourceOfFundInfo = sourceOfFundLookupId
      ? {
          lookupItemName: selected.sourceOfFundInfo?.lookupItemName || null,
          lookupsId: sourceOfFundLookupId,
        }
      : null;

    const paymentMethodLookupId =
      selected.paymentMethodInfo?.lookupItemId ||
      selected.paymentMethodInfo?.lookupsId ||
      null;
    const paymentMethodInfo = paymentMethodLookupId
      ? {
          lookupItemName: selected.paymentMethodInfo?.lookupItemName || null,
          lookupsId: paymentMethodLookupId,
        }
      : null;

    const countryOfResidenceLookupId =
      selected.countryOfResidenceInfo?.lookupItemId ||
      selected.countryOfResidenceInfo?.lookupsId ||
      null;
    const countryOfResidenceInfo = countryOfResidenceLookupId
      ? {
          lookupItemName:
            selected.countryOfResidenceInfo?.lookupItemName || null,
          lookupsId: countryOfResidenceLookupId,
        }
      : null;

    const nationalityOfBeneficiaryLookupId =
      selected.nationalityOfBeneficiaryInfo?.lookupItemId ||
      selected.nationalityOfBeneficiaryInfo?.lookupsId ||
      null;
    const nationalityOfBeneficiaryInfo = nationalityOfBeneficiaryLookupId
      ? {
          lookupItemName:
            selected.nationalityOfBeneficiaryInfo?.lookupItemName || null,
          lookupsId: nationalityOfBeneficiaryLookupId,
        }
      : null;

    const docsAttachedAsObj = getDocsAttachedAsObj();

    if (!selected.isActingOnBehalf) clearOnBehalfFields();
    if (!selected.isRelatedToPEP) clearPEPFields();

    return {
      ...selected,
      occupationInfo,
      sourceOfFundInfo,
      paymentMethodInfo,
      countryOfResidenceInfo,
      nameOfPEP: selected.isRelatedToPEP ? selected.nameOfPEP : null,
      nameOfBeneficiary: selected.isActingOnBehalf
        ? selected.nameOfBeneficiary
        : null,
      natureOfRelationship: selected.isActingOnBehalf
        ? selected.natureOfRelationship
        : null,
      idType: selected.isActingOnBehalf ? selected.idType : null,
      nationalityOfBeneficiaryInfo: selected.isActingOnBehalf
        ? nationalityOfBeneficiaryInfo
        : null,
      isBeneficiaryPEP: selected.isActingOnBehalf
        ? selected.isBeneficiaryPEP
        : null,
      emailAddressOfBeneficiary: selected.isActingOnBehalf
        ? selected.emailAddressOfBeneficiary
        : null,
      contactNumberOfBeneficiary: selected.isActingOnBehalf
        ? selected.contactNumberOfBeneficiary
        : null,
      occupationAsString: null,
      sourceOfFundsAsString: null,
      paymentMethodAsString: null,
      countryOfResidenceAsString: null,
      nationalityOfBeneficiaryInfoAsString: null,
      attachedDocTypes: null,
      ...(docsAttachedAsObj || {}),
    };
  };

  const getKYCCorporateFormDto = () => {
    const paymentMethodLookupId =
      selected.paymentMethodInfo?.lookupItemId ||
      selected.paymentMethodInfo?.lookupsId ||
      null;
    const paymentMethodInfo = paymentMethodLookupId
      ? {
          lookupItemName: selected.paymentMethodInfo?.lookupItemName || null,
          lookupsId: paymentMethodLookupId,
        }
      : null;

    const legalStructureLookupId =
      selected.legalStructureInfo?.lookupItemId ||
      selected.legalStructureInfo?.lookupsId ||
      null;
    const legalStructureInfo = legalStructureLookupId
      ? {
          lookupItemName: selected.legalStructureInfo?.lookupItemName || null,
          lookupsId: legalStructureLookupId,
        }
      : null;

    const companySignatories = selected.companySignatories
      ? selected.companySignatories.map((item) => ({
          companySignatoryId: item.companySignatoryId || 0,
          contactId: item.contactId,
          specimenSignature: item.specimenSignature,
          contactName: null,
        }))
      : [];

    const companyStakeholdersInformation =
      selected.companyStakeholdersInformation
        ? selected.companyStakeholdersInformation.map((item) => ({
            companyStakeholdersInformationId:
              item.companyStakeholdersInformationId || 0,
            contactStakeholderId: item.contactStakeholderId || item.contactId,
            residenceStatus:
              item.residenceStatus?.lookupItemName ||
              item.residenceStatus ||
              null,
            sharePercentage: +item.sharePercentage,
            contactName: null,
          }))
        : [];

    const companyPartnerNamesAndNationalities =
      selected.companyPartnerNamesAndNationalities
        ? selected.companyPartnerNamesAndNationalities.map((item) => ({
            companyPartnerNamesAndNationalityId:
              item.companyPartnerNamesAndNationalityId || 0,
            contactId: item.contactId,
            percentage: +item.percentage,
            contactName: null,
          }))
        : [];

    return {
      ...selected,
      legalStructureInfo,
      paymentMethodInfo,
      companySignatories,
      companyStakeholdersInformation,
      companyPartnerNamesAndNationalities,
      legalStructureAsString: null,
      paymentMethodAsString: null,
      companyPartnerNamesAndNationalitiesAsString: null,
      companySignatoriesAsString: null,
      companyStakeholdersInformationAsString: null,
    };
  };

  const getFormattedDTO = () => {
    const isIndividualContact = contactsType === 1;

    return {
      contactId,
      contactsType,
      kycIndividualFormDto: isIndividualContact
        ? getKYCIndividualFormDto()
        : null,
      kycCorporateFormDto: !isIndividualContact
        ? getKYCCorporateFormDto()
        : null,
    };
  };

  const getDocsAttachedAsList = (formDetails) => {
    const docsAttachedAsList = [
      {
        key: 'hasProofOfAddress',
        label: 'Has Proof Of Address',
        value: formDetails.hasProofOfAddress || false,
      },
      {
        key: 'hasProofOfSourceOfFunds',
        label: 'Has Proof Of Source Of Funds',
        value: formDetails.hasProofOfSourceOfFunds || false,
      },
      {
        key: 'hasProofOfPayment',
        label: 'Has Proof Of Payment',
        value: formDetails.hasProofOfPayment || false,
      },
      {
        key: 'hasDealDocuments',
        label: 'Has Deal Documents',
        value: formDetails.hasDealDocuments || false,
      },
      {
        key: 'hasIdCardOrPassport',
        label: 'Has Id Card Or Passport',
        value: formDetails.hasIdCardOrPassport || false,
      },
    ];

    setSelected({ id: 'attachedDocTypes', value: docsAttachedAsList });
  };

  const getKycFormDetails = async () => {
    setIsLoading({ id: 'get', value: true });

    const res = await GetKycFormDetailsByContactId(contactId, contactsType);
    if (!(res && res.status && res.status !== 200)) {
      setIsLoading({ id: 'get', value: false });
      const isIndividualContact = contactsType === 1;
      const formDetails = isIndividualContact
        ? res?.kycIndividualFormDto
        : res?.kycCorporateFormDto;
      setSelected({ id: 'edit', value: formDetails });
      if (isIndividualContact) getDocsAttachedAsList(formDetails);
      return formDetails;
    } else setSelected({ id: 'edit', value: {} });

    setIsLoading({ id: 'get', value: false });
  };

  const isFormValidated = () => {
    let isValidated = true;

    const isCorporateEmailExist = selected.email;
    const isCorporateRequriedFieldsExist =
      selected.paymentMethodInfo && selected.legalStructureInfo;
    const isPEPRequriedFieldsExist = selected.nameOfPEP;
    const isOnBehalfRequriedFieldsExist =
      selected.nameOfBeneficiary &&
      selected.natureOfRelationship &&
      selected.idType &&
      selected.nationalityOfBeneficiaryInfo &&
      typeof selected.isBeneficiaryPEP === 'boolean' &&
      selected.emailAddressOfBeneficiary &&
      selected.contactNumberOfBeneficiary;

    const showWarning = (message) => {
      if (isNewLayout && verticalNav) {
        verticalNav.setAlertBoxContent({
          display: true,
          variant: 'warning',
          title: message,
          onClose: () => verticalNav.setAlertBoxContent(null),
        });
      } else {
        showWarn(message);
      }
    };

    if (contactsType === 2 && !isCorporateRequriedFieldsExist) {
      showWarning('Please fill the required fields.');
      isValidated = false;
      setCheckFormValidation(true);
    }

    if (
      contactsType === 1 &&
      selected.isActingOnBehalf &&
      !isOnBehalfRequriedFieldsExist
    ) {
      showWarning('Please fill On Behalf fields.');
      isValidated = false;
    }

    if (
      contactsType === 1 &&
      selected.isRelatedToPEP &&
      !isPEPRequriedFieldsExist
    ) {
      showWarning('Please fill PEP relationship field.');
      isValidated = false;
    }

    if (contactsType === 2 && !isCorporateEmailExist) {
      showWarning('Please enter a valid email address.');
      isValidated = false;
    }

    if (isValidated) {
      setCheckFormValidation(false);
    }

    return isValidated;
  };

  const reloadData = async () => {
    const formDetails = await getKycFormDetails();
    return formDetails;
  };

  const updateKycDetails = async ({ isForFormCreate }) => {
    const body = getFormattedDTO();
    const res = await UpdateKycDetails(body);

    if (res === true) {
      if (isNewLayout && verticalNav) {
        verticalNav.setAlertBoxContent({
          display: true,
          variant: 'success',
          title: 'Changes are saved successfully!',
          onClose: () => verticalNav.setAlertBoxContent(null),
        });
      } else if (!isForFormCreate) {
        showSuccess('Changes are saved successfully!');
      }

      const formDetails = await reloadData();
      return formDetails;
    } else {
      if (isNewLayout && verticalNav) {
        verticalNav.setAlertBoxContent({
          display: true,
          variant: 'error',
          title: 'Failed to save changes..',
          onClose: () => verticalNav.setAlertBoxContent(null),
        });
      } else if (!isForFormCreate) {
        showError('Failed to save changes..');
      }
    }
  };

  const getDocsAttachedAsObj = () => {
    const docsAttachedAsObj = {};
    if (selected.attachedDocTypes?.length > 0)
      selected.attachedDocTypes.forEach((item) => {
        docsAttachedAsObj[item.key] = item.value;
      });
    return docsAttachedAsObj;
  };

  const saveHandler = async () => {
    if (!isFormValidated()) return;

    setIsLoading({ id: 'save', value: true });
    await updateKycDetails({});
    setIsLoading({ id: 'save', value: false });
  };

  const discardClickHandler = async () => {
    setIsLoading({ id: 'discard', value: true });
    await reloadData();
    setIsLoading({ id: 'discard', value: false });
  };

  const getClientFormMap = () => {
    const isIndividualContact = contactsType === 1;
    const formMap = isIndividualContact
      ? IndividualKycFormMap
      : CorporateKycFormMap;
    const stringifiedMap = JSON.stringify(formMap);

    const clientFormMap = stringifiedMap.replace(
      /selectInput|readOnly/g,
      'textInput'
    );
    const parsedMap = JSON.parse(clientFormMap);

    return parsedMap;
  };

  const returnFormAsHtml = async ({ showActionButtons }) => {
    let innerHTML = clientFormRef.current?.innerHTML || '';

    // If innerHTML is empty, try to get content from the main form container
    if (!innerHTML.trim()) {
      // Try to get content from the KycForm class directly
      const formElement = document.querySelector('.KycForm');
      if (formElement) {
        innerHTML = formElement.outerHTML;
      }

      // If still empty, try to get from the form wrapper
      if (!innerHTML.trim()) {
        const formWrapper = document.querySelector(
          '.newKycFormsView .KycForm, .KycFormsView .KycForm'
        );
        if (formWrapper) {
          innerHTML = formWrapper.outerHTML;
        }
      }

      // Last resort: get all form content
      if (!innerHTML.trim()) {
        const allFormContent = document.querySelector('#KycForm');
        if (allFormContent) {
          innerHTML = allFormContent.outerHTML;
        }
      }
    }

    if (!innerHTML.trim()) {
      throw new Error('No form content available for printing');
    }

    const KYCFormContent = {
      innerHTML,
      showActionButtons,
    };
    const template =
      isNewLayout && hasNewLayout
        ? NewKYCFormHTMLTemplate
        : KYCFormHTMLTemplate;
    const KYCHtml = await Mustache.render(template, KYCFormContent);

    return KYCHtml;
  };

  const printHandler = async () => {
    try {
      // Wait for form content to be available
      await waitForFormContent();

      const content = await returnFormAsHtml({ showActionButtons: false });

      const iframe = document.getElementById('formPrintIframe');

      if (!iframe) {
        showError(
          'Print functionality is not available. Please refresh the page and try again.'
        );
        return;
      }

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      // Clear any existing content
      iframeDoc.open();
      iframeDoc.write('');
      iframeDoc.close();

      // Write the new content
      iframeDoc.open();
      iframeDoc.write(content);
      iframeDoc.close();

      // Wait for content to load in iframe
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Debug iframe content
      const hasValidContent = debugIframeContent(iframe);

      if (!hasValidContent) {
        // Try writing content differently
        iframeDoc.open();
        iframeDoc.write(
          '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>'
        );
        iframeDoc.write(content);
        iframeDoc.write('</body></html>');
        iframeDoc.close();

        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Ensure iframe is focused and ready
      iframe.contentWindow.focus();
      // Try printing
      try {
        iframe.contentWindow.print();
      } catch {
        showError('Direct print failed, trying alternative method:');

        // Alternative method: open in new window
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (printWindow) {
          printWindow.document.write(content);
          printWindow.document.close();
          printWindow.focus();

          // Wait a bit for content to render
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 1000);
        } else {
          showError(
            'Unable to open print window. Please check if popups are blocked.'
          );
        }
      }
    } catch (error) {
      showError(
        `Failed to print the form: ${error.message}. Please ensure the form is fully loaded and try again.`
      );
    }
  };

  const shareKycDocument = async ({ kycFormId, shareVia }) => {
    if (shareVia === ShareViaEnum.WhatsApp.id && !isWhatsAppNumExist()) return;

    setIsLoading({ id: 'shareForm', value: true });

    const body = {
      kycFormId: kycFormId || selected.kycFormId || null,
      contactId: +contactId,
      shareVia: shareVia,
      kycDocumentContent: await returnFormAsHtml({ showActionButtons: true }),
      isManualKYC: shareVia === ShareViaEnum.Manual.id,
    };

    const res = await ShareKycDocument(body);

    if (res?.isSuccess === true) {
      if (isNewLayout && verticalNav) {
        verticalNav.setAlertBoxContent({
          display: true,
          variant: 'success',
          title:
            shareVia === ShareViaEnum.Manual.id
              ? 'Document shared manually successfully!'
              : 'Shared Successfully!',
          onClose: () => verticalNav.setAlertBoxContent(null),
        });
      } else {
        if (shareVia === ShareViaEnum.Email.id)
          showSuccess('Shared Successfully!');
        if (shareVia === ShareViaEnum.WhatsApp.id) openWhatsApp(res.message);
        if (shareVia === ShareViaEnum.Manual.id)
          showSuccess('Document shared manually successfully!');
      }
    } else {
      if (isNewLayout && verticalNav) {
        verticalNav.setAlertBoxContent({
          display: true,
          variant: 'error',
          title: 'Sharing has failed ..',
          onClose: () => verticalNav.setAlertBoxContent(null),
        });
      } else {
        showError('Sharing has failed ..');
      }
    }

    setIsLoading({ id: 'shareForm', value: false });
  };

  const getWhatsAppNum = () => {
    const activeContact = localStorage.getItem('parentContact');
    const parsedActiveContact = activeContact
      ? JSON.parse(activeContact)
      : null;
    return (
      parsedActiveContact?.whatsapp_mobile?.phone ||
      parsedActiveContact?.landline_number?.phone ||
      null
    );
  };

  const isWhatsAppNumExist = () => {
    const whatsAppNumber = getWhatsAppNum();

    if (!whatsAppNumber) {
      if (isNewLayout && verticalNav) {
        verticalNav.setAlertBoxContent({
          display: true,
          variant: 'warning',
          title: 'This contact has no available WhatsApp number.',
          onClose: () => verticalNav.setAlertBoxContent(null),
        });
      } else {
        showWarn('This contact has no available WhatsApp number.');
      }
      return;
    }

    return whatsAppNumber;
  };

  const openWhatsApp = (msg) => {
    const UrlEncodedMessage = encodeURIComponent(msg);
    const UrlToWhatsApp = `https://wa.me/${getWhatsAppNum()}?text=${UrlEncodedMessage}`;
    window.open(UrlToWhatsApp);
  };

  const shareHandler = async (shareVia) => {
    if (!isFormValidated()) return;
    const isFormIdExist = selected.kycFormId;
    if (isFormIdExist) shareKycDocument({ shareVia });
    else {
      const formDetails = await updateKycDetails({ isForFormCreate: true });
      if (formDetails)
        shareKycDocument({ kycFormId: formDetails.kycFormId, shareVia });
    }
  };

  const checkIsButtonDisabled = () =>
    isLoading.discard || isLoading.get || isLoading.shareForm || isLoading.save;

  // Helper function to wait for form content to be available
  const waitForFormContent = async (maxAttempts = 10) => {
    for (let i = 0; i < maxAttempts; i++) {
      const formElement =
        document.querySelector('.KycForm') ||
        document.querySelector('#KycForm') ||
        clientFormRef.current;

      if (
        formElement &&
        formElement.innerHTML &&
        formElement.innerHTML.trim().length > 1000
      ) {
        return formElement;
      }

      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    throw new Error('Form content not available after maximum attempts');
  };
  // Debug function to check iframe content
  const debugIframeContent = (iframe) => {
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      const bodyContent = iframeDoc.body
        ? iframeDoc.body.innerHTML
        : 'No body found';

      return bodyContent.length > 1000;
    } catch (error) {
      return false;
    }
  };

  // Layout effect to ensure proper ref setup after DOM calculations
  useLayoutEffect(() => {
    if (!isLoading.get && selected && Object.keys(selected).length > 0) {
      // Wait a bit for components to render
      const timer = setTimeout(() => {
        // Try multiple selectors to find the form content
        const formElement =
          document.querySelector('.KycForm') ||
          document.querySelector('#KycForm') ||
          clientFormRef.current;

        if (formElement) {
          formElement.style.visibility = 'visible';
          formElement.style.display = 'block';
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [selected, isLoading.get, contactsType]);

  useEffect(() => {
    getKycFormDetails();
  }, []);
  useEffect(
    () => () => {
      bottomBoxComponentUpdate(null);
    },
    [selected]
  );

  return (
    <div>
      {isNewLayout && hasNewLayout && (
        <div>
          <div className='flexSpaceBetween'>
            <div
              className='buttonActions'
              style={{
                visibility:
                  viewMode === KYC_TAB_MODES.KYC ? 'visible' : 'hidden',
              }}
            >
              <CustomIconButton
                boxShadow='xs'
                size='lg'
                borderRadius='md'
                variant='outlined'
                color='secondary'
                disabled={
                  !(selected?.emailAddress || selected?.email) ||
                  checkIsButtonDisabled()
                }
                onClick={() => {
                  if (selected?.emailAddress || selected?.email)
                    shareHandler(ShareViaEnum.Email.id);
                }}
              >
                <EmailIcon
                  width='20'
                  height='20'
                  fill={!isDarkMode ? '#344054' : '#CECFD2'}
                />
              </CustomIconButton>
              <CustomIconButton
                boxShadow='xs'
                size='lg'
                borderRadius='md'
                variant='outlined'
                disabled={checkIsButtonDisabled()}
                color='secondary'
                onClick={() => {
                  shareHandler(ShareViaEnum.WhatsApp.id);
                }}
              >
                <WhatsappIcon
                  width='20'
                  height='20'
                  fill={!isDarkMode ? '#344054' : '#CECFD2'}
                />
              </CustomIconButton>

              <CustomButton
                boxShadow='xs'
                size='lg'
                variant='outlined'
                color='secondary'
                disabled={checkIsButtonDisabled()}
                onClick={() => shareHandler(ShareViaEnum.Manual.id)}
                startIcon={
                  <ShareIcon
                    width='20'
                    height='20'
                    fill={!isDarkMode ? '#344054' : '#CECFD2'}
                  />
                }
              >
                Manual share
              </CustomButton>
              <CustomButton
                boxShadow='xs'
                size='lg'
                variant='outlined'
                color='secondary'
                onClick={printHandler}
                startIcon={
                  <PrintIcon
                    width='20'
                    height='20'
                    fill={!isDarkMode ? '#344054' : '#CECFD2'}
                  />
                }
              >
                Print
              </CustomButton>
            </div>

            <div>
              <KYCSwitcher
                kycMode={viewMode}
                onChangeViewMode={onChangeViewMode}
              />
            </div>
          </div>
        </div>
      )}

      {isNewLayout && hasNewLayout && viewMode === KYC_TAB_MODES.History && (
        <KYCHistory />
      )}

      {((isNewLayout && hasNewLayout && viewMode === KYC_TAB_MODES.KYC) ||
        !(isNewLayout && hasNewLayout)) && (
        <div
          className={
            isNewLayout && hasNewLayout ? 'newKycFormsView' : 'KycFormsView'
          }
        >
          <div
            className={`d-flex-h-between ${
              isNewLayout && hasNewLayout ? 'mx-0 mt-0' : 'w-75 mx-5 mt-3'
            } fa-center`}
          >
            <div className={isNewLayout && hasNewLayout ? 'psiLogo' : ''}>
              {isPsiAssetsCRM ? (
                <img
                  style={{ alignSelf: 'flex-start' }}
                  src='/psiAssetsLogo.svg'
                  width='84'
                  height='76'
                />
              ) : (
                <img
                  style={{ alignSelf: 'flex-start' }}
                  src='/psiInvestmentLogo.svg'
                  width='170'
                  height='76'
                />
              )}

              <PageHeading
                headerTitle={'Know Your Client Form (KYC)'}
                subTitle={`All details must be completed by each Individual Client.`}
                headerTitleClasses={'fz-18 mb-3'}
                wrapperClasses={
                  !(isNewLayout && hasNewLayout) ? 'pt-2 w-75' : ''
                }
                subTitleClasses={'fz-16 m-0 ml-0'}
              />
            </div>
            {!(isNewLayout && hasNewLayout) && (
              <div className='form-top-actions d-flex-default fj-center my-4'>
                <ButtonBase
                  onClick={printHandler}
                  className='btns theme-propx outlined'
                >
                  <span
                    className='mdi mdi-printer'
                    title='Print form'
                  ></span>
                </ButtonBase>

                <ButtonBase
                  className='btns theme-propx solid mr-0'
                  onClick={() => shareHandler(ShareViaEnum.WhatsApp.id)}
                  disabled={checkIsButtonDisabled()}
                >
                  <span
                    className='mdi mdi-whatsapp'
                    title='Share via whatsapp'
                  ></span>
                </ButtonBase>
                <ButtonBase
                  className='btns theme-propx solid mr-0'
                  onClick={() => shareHandler(ShareViaEnum.Email.id)}
                  disabled={checkIsButtonDisabled()}
                >
                  <span
                    className='mdi mdi-email'
                    title='Share via email'
                  ></span>
                </ButtonBase>
                <ButtonBase
                  className='btns theme-propx solid mr-0'
                  onClick={() => shareHandler(ShareViaEnum.Manual.id)}
                  disabled={checkIsButtonDisabled()}
                >
                  <span
                    className='mdi mdi-file-document-outline'
                    title='Share KYC Manual'
                  ></span>
                </ButtonBase>
              </div>
            )}
          </div>

          {!isLoading?.get && contactsType === 1 ? (
            <IndividualForm
              clientFormRef={clientFormRef}
              clientFormMap={getClientFormMap()}
              selected={selected}
              onSelectedChange={(key, selectValue) =>
                setSelected({ id: key, value: selectValue })
              }
            />
          ) : null}
          {!isLoading?.get && contactsType === 2 ? (
            <CorporateForm
              clientFormRef={clientFormRef}
              clientFormMap={getClientFormMap()}
              selected={selected}
              checkFormValidation={checkFormValidation}
              onSelectedChange={(key, selectValue) =>
                setSelected({ id: key, value: selectValue })
              }
            />
          ) : null}
          <iframe
            id='formPrintIframe'
            style={{
              minHeight: 500,
              display: 'none',
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            width='100%'
            height='100%'
          />

          {isNewLayout && hasNewLayout ? (
            <div className='actions'>
              <CustomButton
                variant='outlined'
                color='secondary'
                onClick={discardClickHandler}
              >
                {t(`${translationPath}Discard`)}
              </CustomButton>
              <CustomButton
                type='submit'
                variant='contained'
                color='primary'
                onClick={saveHandler}
                disabled={checkIsButtonDisabled()}
              >
                {t(`${translationPath}Save`)}
              </CustomButton>
            </div>
          ) : (
            <div className='mx-5 w-75 d-flex fj-center my-4'>
              <ButtonBase
                onClick={discardClickHandler}
                className='btns theme-propx outlined'
                disabled={checkIsButtonDisabled()}
              >
                {t(`${translationPath}Discard`)}
                {isLoading.discard ? (
                  <img
                    src={btnLoader}
                    className='ml-1'
                    width='12'
                    height='12'
                  />
                ) : null}
              </ButtonBase>

              <ButtonBase
                className='btns theme-propx solid mr-0'
                onClick={saveHandler}
                disabled={checkIsButtonDisabled()}
              >
                {t(`${translationPath}Save`)}
                {isLoading.save ? (
                  <img
                    src={btnLoader}
                    className='ml-1'
                    width='12'
                    height='12'
                  />
                ) : null}
              </ButtonBase>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
